import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, BehaviorSubject} from 'rxjs';
import {AccountService} from 'app/core/auth/account.service';
import {Store} from '@ngxs/store';
import {GetConfig} from 'app/blocks/store/actions/common-actions';
import {CommonUtilsService} from 'app/common/common-utils-service';
import {BrowserAgent} from '@newrelic/browser-agent/loaders/browser-agent';
import {SERVER_API_URL} from 'app/app.constants';
import versionJson from '../../../../version.json';

@Injectable()
export class Principal {
    private userIdentity: any;
    private contactInfoId: number;
    private organisationIds: number[];
    private subContractorOrganisation = false;
    private subContractorSubject = new BehaviorSubject<boolean>(false);
    // Emits whenever the sub-contractor flag is (re)resolved; components subscribe so the UI updates
    // even though the flag is resolved asynchronously after identity().
    isSubContractorUser$ = this.subContractorSubject.asObservable();
    private authenticated = false;
    private authenticationState = new BehaviorSubject<any>(null);

    constructor(
        private account: AccountService,
        private store: Store,
        private commonUtilsService: CommonUtilsService,
        private http: HttpClient,
        private newrelicAgent?: BrowserAgent
    ) {
        this.authenticationState.subscribe((identity) => {
            if (identity) {
                this.newrelicAgent?.setCustomAttribute('tenant', identity.tenantId);
                this.newrelicAgent?.setCustomAttribute('appVersion', versionJson.appVersion);
                this.newrelicAgent?.setCustomAttribute('userId', identity.id);
                this.newrelicAgent?.setCustomAttribute('role', identity.roles);
                this.newrelicAgent?.setCustomAttribute('authority', identity.authorities);
                this.newrelicAgent?.setCustomAttribute('username', identity.firstName + ' ' + identity.lastName);
                this.newrelicAgent?.setCustomAttribute('email', identity.email);
            }
        });
    }

    authenticate(identity): void {
        this.userIdentity = identity;
        this.authenticated = identity !== null;
        this.authenticationState.next(this.userIdentity);
    }

    hasAnyAuthority(authorities: string[]): Promise<boolean> {
        return Promise.resolve(this.hasAnyAuthorityDirect(authorities));
    }

    hasAnyAuthorityDirect(authorities: string[]): boolean {
        if (!this.authenticated || !this.userIdentity || !this.userIdentity.authorities) {
            return false;
        }

        for (let i = 0; i < authorities.length; i++) {
            if (this.userIdentity.authorities.includes(authorities[i])) {
                return true;
            }
        }

        return false;
    }

    hasAuthority(authority: string): Promise<boolean> {
        if (!this.authenticated) {
            return Promise.resolve(false);
        }

        return this.identity().then(
            (id) => {
                return Promise.resolve(id.authorities && id.authorities.includes(authority));
            },
            () => {
                return Promise.resolve(false);
            }
        );
    }

    identity(force?: boolean): Promise<any> {
        if (force === true) {
            this.userIdentity = undefined;
        }

        // check and see if we have retrieved the userIdentity data from the server.
        // if we have, reuse it by immediately resolving
        if (this.userIdentity) {
            return Promise.resolve(this.userIdentity);
        }

        // retrieve the userIdentity data from the server, update the identity object, and then resolve.
        return this.account
            .get()
            .toPromise()
            .then((response) => {
                const account = response.body;
                if (account) {
                    this.userIdentity = account;
                    this.authenticated = true;
                    this.contactInfoId = account['contactInfoId'];
                    this.organisationIds = account['organisationIds'];
                    this.store.dispatch(new GetConfig());
                } else {
                    this.userIdentity = null;
                    this.authenticated = false;
                }
                this.authenticationState.next(this.userIdentity);
                // Resolve the sub-contractor flag from the customer's own organisation before the identity promise settles,
                // so downstream components can read isSubContractorUser() synchronously.
                return this.resolveSubContractorFlag().then(() => this.userIdentity);
            })
            .catch((err) => {
                this.userIdentity = null;
                this.authenticated = false;
                this.authenticationState.next(this.userIdentity);
                return null;
            });
    }

    isAuthenticated(): boolean {
        return this.authenticated;
    }

    isIdentityResolved(): boolean {
        return this.userIdentity !== undefined;
    }

    getOrganisationIds(): number[] {
        return this.organisationIds;
    }

    getContactInfoId(): number {
        return this.contactInfoId;
    }

    isUserCustomer(): boolean {
        return this.userIdentity && this.userIdentity.authorities.includes('ROLE_CUSTOMER');
    }

    isSubContractorUser(): boolean {
        return this.isUserCustomer() && this.subContractorOrganisation;
    }

    // Frontend-only detection: a customer any of whose linked organisations is flagged as a sub-contractor.
    // Loads the organisations on identity() and reads their subContractor flag (no dedicated backend field).
    private resolveSubContractorFlag(): Promise<void> {
        this.subContractorOrganisation = false;
        const orgIds = this.organisationIds ?? [];
        if (!this.isUserCustomer() || orgIds.length === 0) {
            this.subContractorSubject.next(false);
            return Promise.resolve();
        }
        return Promise.all(
            orgIds.map((orgId) =>
                this.http
                    .get<any>(`${SERVER_API_URL}api/organisations/${orgId}`)
                    .toPromise()
                    .then((organisation) => !!organisation?.subContractor)
                    .catch(() => false)
            )
        )
            .then((results) => {
                this.subContractorOrganisation = results.some(Boolean);
            })
            .catch(() => {
                this.subContractorOrganisation = false;
            })
            .finally(() => {
                this.subContractorSubject.next(this.isSubContractorUser());
            });
    }

    getAuthenticationState(): Observable<any> {
        return this.authenticationState.asObservable();
    }

    getImageUrl(): string {
        return this.isIdentityResolved() ? this.userIdentity.imageUrl : null;
    }

    getTenantId(): string {
        return this.userIdentity ? this.userIdentity.tenantId : null;
    }
}
